/**
 * BespokeBridge – Community Management Module API
 * Covers Tool 1: Volunteer Management (profiles, availability, shifts, hours, milestones)
 *         Tool 2: Member Management (profiles, segmentation, directory, engagement)
 *
 * All Firestore paths use `organizations/{orgId}/` consistent with the rest of the app.
 */
import {
  collection, doc, getDoc, getDocs, addDoc, updateDoc, deleteDoc,
  query, where, orderBy, serverTimestamp, increment, Timestamp
} from 'firebase/firestore';
import { db } from '../utils/firebase';

// ─── Low-level helpers ──────────────────────────────────────────────────────
const orgCol = (orgId, col)      => collection(db, 'organizations', orgId, col);
const orgDoc = (orgId, col, id)  => doc(db, 'organizations', orgId, col, id);
const rootCol = (col)            => collection(db, col);
const rootDoc = (col, id)        => doc(db, col, id);
const now    = ()                => serverTimestamp();
const toArr  = snap              => snap.docs.map(d => ({ id: d.id, ...d.data() }));
const byCreatedAtDesc = (a, b) => {
  const da = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt || 0);
  const db_ = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt || 0);
  return db_ - da;
};

// ─── QR helper (reuses qrserver.com) ───────────────────────────────────────
export const getQRCodeUrl = (token, size = 220) =>
  `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&data=${encodeURIComponent(token)}&margin=10`;

// ─── Date helpers ───────────────────────────────────────────────────────────
export const fmtDate = (ts) => {
  if (!ts) return '—';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleDateString('en-SG', { day: '2-digit', month: 'short', year: 'numeric' });
};
export const fmtDateTime = (ts) => {
  if (!ts) return '—';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleString('en-SG', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};
export const todayStr = () => new Date().toISOString().split('T')[0];

// ═══════════════════════════════════════════════════════════════════════════
// ENUMS & CONSTANTS
// ═══════════════════════════════════════════════════════════════════════════

export const VOLUNTEER_SKILLS = [
  { value: 'counseling',       label: 'Counseling' },
  { value: 'financial_advice', label: 'Financial Advice' },
  { value: 'event_planning',   label: 'Event Planning' },
  { value: 'legal',            label: 'Legal' },
  { value: 'tech_support',     label: 'Tech Support' },
  { value: 'other',            label: 'Other' },
];

export const LANGUAGES = [
  { value: 'english',  label: 'English' },
  { value: 'mandarin', label: 'Mandarin' },
  { value: 'malay',    label: 'Malay' },
  { value: 'tamil',    label: 'Tamil' },
];

export const SINGAPORE_DISTRICTS = [
  'Ang Mo Kio', 'Bedok', 'Bishan', 'Boon Lay', 'Bukit Batok', 'Bukit Merah',
  'Bukit Panjang', 'Bukit Timah', 'Central Area', 'Choa Chu Kang', 'Clementi',
  'Geylang', 'Hougang', 'Jurong East', 'Jurong West', 'Kallang',
  'Mandai', 'Marine Parade', 'Novena', 'Pasir Ris', 'Punggol', 'Queenstown',
  'Sembawang', 'Sengkang', 'Serangoon', 'Tampines', 'Toa Payoh',
  'Woodlands', 'Yishun',
];

export const SHIFT_CATEGORIES = [
  { value: 'support_group', label: 'Support Group' },
  { value: 'event',         label: 'Event' },
  { value: 'admin',         label: 'Admin' },
  { value: 'outreach',      label: 'Outreach' },
  { value: 'other',         label: 'Other' },
];

export const MILESTONES = [
  { key: '10hrs',  threshold: 10,  label: 'Getting Started', icon: '◦', range: '1–50 hrs'    },
  { key: '50hrs',  threshold: 50,  label: 'Committed',       icon: '◉', range: '51–100 hrs'  },
  { key: '100hrs', threshold: 100, label: 'Champion',        icon: '★', range: '101–200 hrs' },
  { key: '200hrs', threshold: 200, label: 'Hero',            icon: '◆', range: '201–300 hrs' },
  { key: '1year',  threshold: null, label: 'Veteran',        icon: '✦', range: '>300 hrs'    },
];

export const MEMBER_WIDOW_STAGES = [
  { value: 'current', label: 'Current (0–3 years)' },
  { value: 'past',    label: 'Past (3+ years)' },
];
export const MEMBER_FINANCIAL_STATES = [
  { value: 'needs_assistance',  label: 'Needs Assistance' },
  { value: 'stable',            label: 'Stable' },
  { value: 'willing_to_help',   label: 'Willing to Help' },
];
export const MEMBER_EMPLOYMENT_STATUSES = [
  { value: 'employed_full',  label: 'Employed (Full-time)' },
  { value: 'employed_part',  label: 'Employed (Part-time)' },
  { value: 'self_employed',  label: 'Self-employed' },
  { value: 'unemployed',     label: 'Unemployed' },
  { value: 'retired',        label: 'Retired' },
  { value: 'student',        label: 'Student' },
];
export const MEMBER_INTERESTS = [
  { value: 'support_groups',       label: 'Support Groups' },
  { value: 'financial_workshops',  label: 'Financial Workshops' },
  { value: 'social_events',        label: 'Social Events' },
  { value: 'skills_training',      label: 'Skills Training' },
  { value: 'mentorship',           label: 'Mentorship' },
];

// ═══════════════════════════════════════════════════════════════════════════
// TOOL 1 — VOLUNTEER PROFILES
// ═══════════════════════════════════════════════════════════════════════════

const VOLUNTEER_DEFAULTS = {
  displayName: '',
  email: '',
  phone: '',
  skills: [],
  languages: [],
  district: '',
  bio: '',
  notes: '',
  status: 'active',
  totalHours: 0,
  totalShiftsCompleted: 0,
  totalMembersServed: 0,
  totalEventsSupported: 0,
  currentMilestone: null,
  earnedMilestones: [],
};

export const getVolunteers = async (orgId, statusFilter = null) => {
  const base = rootCol('volunteers');
  const q = statusFilter
    ? query(base, where('orgId', '==', orgId), where('status', '==', statusFilter))
    : query(base, where('orgId', '==', orgId));
  const snap = await getDocs(q);
  const docs = snap.docs.map(d => ({ ...VOLUNTEER_DEFAULTS, ...d.data(), id: d.id }));
  return docs.sort(byCreatedAtDesc);
};

/** Returns a map of volunteerId → approved role array for the org (reads from volunteers.volunteerRoles field) */
export const getVolunteerApprovedRolesMap = async (orgId) => {
  const q = query(rootCol('volunteers'), where('orgId', '==', orgId));
  const snap = await getDocs(q);
  const map = {};
  snap.docs.forEach(d => {
    const roles = d.data().volunteerRoles || [];
    if (roles.length > 0) map[d.id] = roles;
  });
  return map;
};

export const getVolunteer = async (orgId, volunteerId) => {
  const d = await getDoc(rootDoc('volunteers', volunteerId));
  return d.exists() ? { id: d.id, ...d.data() } : null;
};

export const createVolunteer = async (orgId, data, createdBy) => {
  const ref = await addDoc(rootCol('volunteers'), {
    ...data,
    orgId,
    status: data.adminCreated ? 'active' : 'pending_approval',
    volunteerRoles: [],
    pendingVolunteerRoles: {},
    roleAvailability: {},
    totalHours: 0,
    totalShiftsCompleted: 0,
    totalMembersServed: 0,
    totalEventsSupported: 0,
    currentMilestone: null,
    earnedMilestones: [],
    createdBy,
    createdAt: now(),
    updatedAt: now(),
  });
  return ref.id;
};

export const updateVolunteer = async (orgId, volunteerId, data) => {
  await updateDoc(rootDoc('volunteers', volunteerId), { ...data, updatedAt: now() });
};

export const updateVolunteerStatus = async (orgId, volunteerId, status) => {
  const updates = { status, updatedAt: now() };
  if (status === 'active') updates.onboardedAt = now();
  await updateDoc(rootDoc('volunteers', volunteerId), updates);
};

// ─── Volunteer Availability ─────────────────────────────────────────────────

export const getVolunteerAvailability = async (orgId, volunteerId) => {
  const q = query(
    orgCol(orgId, 'volunteerAvailability'),
    where('volunteerId', '==', volunteerId),
    orderBy('createdAt', 'desc')
  );
  return toArr(await getDocs(q));
};

export const addAvailability = async (orgId, volunteerId, data) => {
  const ref = await addDoc(orgCol(orgId, 'volunteerAvailability'), {
    ...data,
    volunteerId,
    createdAt: now(),
    updatedAt: now(),
  });
  return ref.id;
};

export const updateAvailability = async (orgId, availabilityId, data) => {
  await updateDoc(orgDoc(orgId, 'volunteerAvailability', availabilityId), { ...data, updatedAt: now() });
};

export const deleteAvailability = async (orgId, availabilityId) => {
  await deleteDoc(orgDoc(orgId, 'volunteerAvailability', availabilityId));
};

// ═══════════════════════════════════════════════════════════════════════════
// SHIFT MANAGEMENT
// ═══════════════════════════════════════════════════════════════════════════

export const getShifts = async (orgId, statusFilter = null) => {
  const base = orgCol(orgId, 'shifts');
  const q = statusFilter
    ? query(base, where('status', '==', statusFilter), orderBy('date', 'asc'))
    : query(base, orderBy('date', 'asc'));
  return toArr(await getDocs(q));
};

export const getShift = async (orgId, shiftId) => {
  const d = await getDoc(orgDoc(orgId, 'shifts', shiftId));
  return d.exists() ? { id: d.id, ...d.data() } : null;
};

export const createShift = async (orgId, data, adminId) => {
  const ref = await addDoc(orgCol(orgId, 'shifts'), {
    ...data,
    signedUpCount: 0,
    status: 'open',
    createdBy: adminId,
    createdAt: now(),
    updatedAt: now(),
  });
  return ref.id;
};

export const updateShift = async (orgId, shiftId, data) => {
  await updateDoc(orgDoc(orgId, 'shifts', shiftId), { ...data, updatedAt: now() });
};

export const deleteShift = async (orgId, shiftId) => {
  await deleteDoc(orgDoc(orgId, 'shifts', shiftId));
};

export const cancelShift = async (orgId, shiftId, reason, adminId) => {
  await updateDoc(orgDoc(orgId, 'shifts', shiftId), {
    status: 'cancelled',
    cancellationReason: reason,
    cancelledBy: adminId,
    cancelledAt: now(),
    updatedAt: now(),
  });
};

export const completeShift = async (orgId, shiftId, attendeeCount, adminId) => {
  await updateDoc(orgDoc(orgId, 'shifts', shiftId), {
    status: 'completed',
    attendeeCount: attendeeCount || 0,
    completedBy: adminId,
    completedAt: now(),
    updatedAt: now(),
  });
};

/** ICS download for a shift */
export const generateShiftICS = (shift) => {
  const dateStr = shift.date || '';                     // 'YYYY-MM-DD'
  const startStr = shift.startTime || '09:00';         // 'HH:MM'
  const endStr   = shift.endTime   || '10:00';

  const dtFormat = (date, time) => {
    const [y, m, d] = date.split('-');
    const [h, min]  = time.split(':');
    return `${y}${m}${d}T${h}${min}00`;
  };

  return [
    'BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//BespokeBridge//Shifts//EN',
    'BEGIN:VEVENT',
    `UID:${shift.id}@bespokebridge-shift`,
    `DTSTART:${dtFormat(dateStr, startStr)}`,
    `DTEND:${dtFormat(dateStr, endStr)}`,
    `SUMMARY:${shift.title}`,
    `DESCRIPTION:${(shift.description || '').replace(/\n/g, '\\n')}`,
    `LOCATION:${shift.location || ''}`,
    'END:VEVENT', 'END:VCALENDAR',
  ].join('\r\n');
};

// ─── Shift Assignments ──────────────────────────────────────────────────────

export const getShiftAssignments = async (orgId, shiftId) => {
  const q = query(
    orgCol(orgId, 'shiftAssignments'),
    where('shiftId', '==', shiftId),
    orderBy('signedUpAt', 'asc')
  );
  return toArr(await getDocs(q));
};

export const getVolunteerAssignments = async (orgId, volunteerId) => {
  const q = query(
    orgCol(orgId, 'shiftAssignments'),
    where('volunteerId', '==', volunteerId),
    orderBy('signedUpAt', 'desc')
  );
  return toArr(await getDocs(q));
};

export const signUpForShift = async (orgId, shiftId, volunteerId, volunteerName) => {
  const shift = await getShift(orgId, shiftId);
  if (!shift) throw new Error('Shift not found');
  if (['filled', 'cancelled', 'completed'].includes(shift.status)) {
    throw new Error(`Shift is ${shift.status} and not accepting sign-ups`);
  }
  if (shift.signedUpCount >= shift.maxVolunteers) throw new Error('This shift is full');

  // Conflict detection: same date, overlapping time window
  const myAssignments = await getVolunteerAssignments(orgId, volunteerId);
  const sameDay = myAssignments.filter(
    a => !['cancelled', 'no_show'].includes(a.status) && a.shiftDate === shift.date
  );
  for (const existing of sameDay) {
    if (existing.shiftStartTime < shift.endTime && existing.shiftEndTime > shift.startTime) {
      throw new Error(`Time conflict with "${existing.shiftTitle}" (${existing.shiftStartTime}–${existing.shiftEndTime})`);
    }
  }

  const ref = await addDoc(orgCol(orgId, 'shiftAssignments'), {
    shiftId,
    volunteerId,
    volunteerName: volunteerName || '',
    shiftDate:      shift.date,
    shiftTitle:     shift.title,
    shiftStartTime: shift.startTime,
    shiftEndTime:   shift.endTime,
    shiftLocation:  shift.location || '',
    status:         'signed_up',
    checkInTime:    null,
    checkOutTime:   null,
    hoursLogged:    null,
    checkInMethod:  null,
    signedUpAt:     now(),
    updatedAt:      now(),
  });

  // Increment counter + auto-fill
  const newCount = (shift.signedUpCount || 0) + 1;
  const statusUpdate = newCount >= shift.maxVolunteers ? 'filled' : shift.status;
  await updateDoc(orgDoc(orgId, 'shifts', shiftId), {
    signedUpCount: increment(1),
    status: statusUpdate,
    updatedAt: now(),
  });

  return ref.id;
};

export const cancelSignUp = async (orgId, assignmentId) => {
  const aRef = orgDoc(orgId, 'shiftAssignments', assignmentId);
  const aSnap = await getDoc(aRef);
  if (!aSnap.exists()) throw new Error('Assignment not found');

  const a = aSnap.data();
  await updateDoc(aRef, { status: 'cancelled', cancelledAt: now(), updatedAt: now() });

  // Decrement + re-open if was filled
  const sRef = orgDoc(orgId, 'shifts', a.shiftId);
  const sSnap = await getDoc(sRef);
  if (sSnap.exists()) {
    const s = sSnap.data();
    const newCount = Math.max(0, (s.signedUpCount || 1) - 1);
    await updateDoc(sRef, {
      signedUpCount: newCount,
      status: s.status === 'filled' ? 'open' : s.status,
      updatedAt: now(),
    });
  }
};

export const checkInVolunteer = async (orgId, assignmentId, adminId = null, method = 'manual') => {
  await updateDoc(orgDoc(orgId, 'shiftAssignments', assignmentId), {
    status: 'checked_in',
    checkInTime: now(),
    checkInMethod: method,
    checkInBy: adminId || null,
    updatedAt: now(),
  });
};

export const checkOutVolunteer = async (orgId, assignmentId) => {
  const aRef = orgDoc(orgId, 'shiftAssignments', assignmentId);
  const aSnap = await getDoc(aRef);
  if (!aSnap.exists()) throw new Error('Assignment not found');

  const a = aSnap.data();
  const checkInMs = a.checkInTime?.toMillis
    ? a.checkInTime.toMillis()
    : (a.checkInTime?.toDate ? a.checkInTime.toDate().getTime() : null);

  if (!checkInMs) throw new Error('No check-in time recorded');

  const nowMs = Date.now();
  const rawHours = (nowMs - checkInMs) / 3600000;
  const hoursLogged = Math.max(0.25, Math.ceil(rawHours * 4) / 4); // round up to nearest 0.25

  await updateDoc(aRef, {
    status:       'completed',
    checkOutTime: Timestamp.fromMillis(nowMs),
    hoursLogged,
    updatedAt:    now(),
  });

  // Update volunteer aggregate hours + check milestones
  if (a.volunteerId) {
    const vRef = rootDoc('volunteers', a.volunteerId);
    const vSnap = await getDoc(vRef);
    if (vSnap.exists()) {
      const v = vSnap.data();
      const prevHours = v.totalHours || 0;
      const newHours  = prevHours + hoursLogged;
      const newEarned = checkNewMilestones(prevHours, newHours, v.onboardedAt, v.earnedMilestones || []);
      await updateDoc(vRef, {
        totalHours:           newHours,
        totalShiftsCompleted: increment(1),
        earnedMilestones:     newEarned.all,
        currentMilestone:     newEarned.newest || v.currentMilestone || null,
        updatedAt:            now(),
      });
    }
  }

  return hoursLogged;
};

export const markNoShow = async (orgId, assignmentId) => {
  await updateDoc(orgDoc(orgId, 'shiftAssignments', assignmentId), {
    status: 'no_show', updatedAt: now(),
  });
};

// ─── QR Sessions ────────────────────────────────────────────────────────────

export const generateQRSession = async (orgId, shiftId, adminId) => {
  const shift = await getShift(orgId, shiftId);
  if (!shift) throw new Error('Shift not found');

  const token    = `${orgId}-${shiftId}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
  const validFrom  = new Date();
  const validUntil = new Date(validFrom.getTime() + 12 * 3600000); // 12 hr window

  const ref = await addDoc(orgCol(orgId, 'qrSessions'), {
    shiftId,
    token,
    validFrom:  Timestamp.fromDate(validFrom),
    validUntil: Timestamp.fromDate(validUntil),
    createdBy:  adminId,
    createdAt:  now(),
  });

  return { id: ref.id, token, validUntil };
};

export const processQRCheckin = async (orgId, token, volunteerId) => {
  const q = query(orgCol(orgId, 'qrSessions'), where('token', '==', token));
  const snap = await getDocs(q);
  if (snap.empty) return { success: false, error: 'QR code not recognised' };

  const session = { id: snap.docs[0].id, ...snap.docs[0].data() };
  const expiry  = session.validUntil?.toDate ? session.validUntil.toDate() : new Date(session.validUntil);
  if (new Date() > expiry) return { success: false, error: 'QR code has expired' };

  // Find assignment for this volunteer + shift
  const aq = query(
    orgCol(orgId, 'shiftAssignments'),
    where('shiftId', '==', session.shiftId),
    where('volunteerId', '==', volunteerId)
  );
  const aSnap = await getDocs(aq);
  if (aSnap.empty) return { success: false, error: 'You are not signed up for this shift' };

  const assignment = { id: aSnap.docs[0].id, ...aSnap.docs[0].data() };
  if (assignment.status === 'checked_in' || assignment.status === 'completed') {
    return { success: false, error: 'Already checked in', already: true };
  }
  if (assignment.status === 'cancelled') return { success: false, error: 'Your sign-up was cancelled' };

  await checkInVolunteer(orgId, assignment.id, null, 'qr_code');
  return { success: true, assignmentId: assignment.id, shiftTitle: assignment.shiftTitle };
};

// ─── Hours ──────────────────────────────────────────────────────────────────

export const getVolunteerHoursBreakdown = async (orgId, volunteerId, periodStart = null, periodEnd = null) => {
  const all = await getVolunteerAssignments(orgId, volunteerId);
  let completed = all.filter(a => a.status === 'completed' && a.hoursLogged);
  if (periodStart) completed = completed.filter(a => (a.shiftDate || '') >= periodStart);
  if (periodEnd)   completed = completed.filter(a => (a.shiftDate || '') <= periodEnd);
  return completed;
};

export const generateHoursCSV = (assignments, volunteerName = '') => {
  const headers = ['Date', 'Shift Title', 'Location', 'Start', 'End', 'Hours'];
  const rows = assignments.map(a => [
    a.shiftDate || '',
    a.shiftTitle || '',
    a.shiftLocation || '',
    a.shiftStartTime || '',
    a.shiftEndTime || '',
    a.hoursLogged || 0,
  ]);
  return [headers, ...rows].map(r =>
    r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')
  ).join('\n');
};

// ─── Milestones ─────────────────────────────────────────────────────────────

export const checkNewMilestones = (prevHours, newHours, onboardedAt, alreadyEarned = []) => {
  const earned = [...alreadyEarned];
  let newest = null;

  for (const m of MILESTONES) {
    if (!m.threshold) continue;
    if (!earned.includes(m.key) && prevHours < m.threshold && newHours >= m.threshold) {
      earned.push(m.key);
      newest = m.key;
    }
  }

  // 1-year milestone
  if (!earned.includes('1year') && onboardedAt) {
    const d = onboardedAt.toDate ? onboardedAt.toDate() : new Date(onboardedAt);
    if ((Date.now() - d.getTime()) / (365.25 * 86400000) >= 1) {
      earned.push('1year');
      if (!newest) newest = '1year';
    }
  }

  return { all: earned, newest };
};

export const getEarnedMilestones = (volunteer) => {
  const hours   = volunteer.totalHours || 0;
  const earned  = volunteer.earnedMilestones || [];
  const display = [];

  for (const m of MILESTONES) {
    if (m.threshold && hours >= m.threshold) display.push(m);
    else if (!m.threshold && earned.includes('1year')) display.push(m);
  }
  return display;
};

// ═══════════════════════════════════════════════════════════════════════════
// TOOL 2 — MEMBER PROFILES
// ═══════════════════════════════════════════════════════════════════════════

export const getMembers = async (orgId) => {
  const q = query(rootCol('members'), where('orgId', '==', orgId));
  const snap = await getDocs(q);
  return toArr(snap).sort(byCreatedAtDesc);
};

export const getMember = async (orgId, memberId) => {
  const d = await getDoc(rootDoc('members', memberId));
  return d.exists() ? { id: d.id, ...d.data() } : null;
};

const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];

const dobToMonthName = (dob) => {
  if (!dob) return null;
  const parts = String(dob).split('-');
  if (parts.length < 2) return null;
  const monthIndex = parseInt(parts[1], 10) - 1;
  return MONTH_NAMES[monthIndex] || null;
};

export const createMember = async (orgId, data, adminId) => {
  const scoreData = calculateEngagementScore({ ...data, eventsAttendedCount: 0, lastActiveAt: null });
  const ref = await addDoc(rootCol('members'), {
    ...data,
    orgId,
    status:             'active',
    profileVisibility:  data.profileVisibility  || 'private',
    contactPreference:  data.contactPreference   || 'admin_only',
    engagementScore:    scoreData.score,
    engagementTier:     scoreData.tier,
    eventsAttendedCount: 0,
    noShowCount:        0,
    programScore:       0,
    tags:               data.tags || [],
    interests:          data.interests || [],
    lastActiveAt:       now(),
    createdBy:          adminId,
    dateJoined:         data.dateJoined || new Date().toISOString().split('T')[0],
    memberBirthMonth:   dobToMonthName(data.dateOfBirth),
    createdAt:          now(),
    updatedAt:          now(),
  });
  return ref.id;
};

export const updateMember = async (orgId, memberId, data) => {
  const update = { ...data, updatedAt: now() };
  if (data.dateOfBirth !== undefined) {
    update.memberBirthMonth = dobToMonthName(data.dateOfBirth);
  }
  await updateDoc(rootDoc('members', memberId), update);
};

export const updateMemberActivity = async (orgId, memberId) => {
  const member = await getMember(orgId, memberId);
  if (!member) return;
  const updated = { ...member, lastActiveAt: { toDate: () => new Date() } };
  const { score, tier } = calculateEngagementScore(updated);
  await updateDoc(rootDoc('members', memberId), {
    lastActiveAt:    now(),
    engagementScore: score,
    engagementTier:  tier,
    updatedAt:       now(),
  });
};

// ─── Engagement Score ────────────────────────────────────────────────────────

export const calculateEngagementScore = (member) => {
  // EventAttendanceScore (0–100)
  const events = member.eventsAttendedCount || 0;
  const eventScore =
    events === 0 ? 0 :
    events === 1 ? 30 :
    events === 2 ? 60 : 100;

  // RecencyScore (0–100)
  let recencyScore = 0;
  if (member.lastActiveAt) {
    const last = member.lastActiveAt.toDate
      ? member.lastActiveAt.toDate()
      : new Date(member.lastActiveAt);
    const days = (Date.now() - last.getTime()) / 86400000;
    if      (days <= 14) recencyScore = 100;
    else if (days <= 30) recencyScore = 75;
    else if (days <= 60) recencyScore = 40;
    else if (days <= 90) recencyScore = 15;
    else                 recencyScore = 0;
  }

  // ProgramScore (0–100) — cached on member doc
  const progScore = member.programScore || 0;

  const score = Math.round(eventScore * 0.5 + recencyScore * 0.3 + progScore * 0.2);
  const tier  =
    score >= 70 ? 'high'   :
    score >= 40 ? 'medium' : 'low';

  return { score, tier, eventScore, recencyScore, progScore };
};

// ─── Member Directory ─────────────────────────────────────────────────────────

/** Returns role-filtered member list for the directory */
export const getMemberDirectory = async (orgId, requestingRole = 'member') => {
  const all = await getMembers(orgId);
  if (requestingRole === 'admin') return all;
  // Volunteer + Member viewers: public profiles only
  return all.filter(m => m.profileVisibility === 'public');
};

/** Public-safe fields for peer/volunteer directory view */
export const maskMemberForDirectory = (member, requestingRole = 'member') => {
  if (requestingRole === 'admin') return member;
  const { firstName, lastName, profilePhotoUrl, district, interests,
          contactPreference, id, status, engagementTier } = member;
  const base = { id, firstName, lastName, profilePhotoUrl, district,
                 interests, contactPreference, status, engagementTier };
  return base;
};

// ─── Client-side Filtering ────────────────────────────────────────────────────

function matchSingleFilter(member, f) {
  const fv = member[f.field];
  const v  = f.value;
  switch (f.operator) {
    case 'equals':       return fv === v;
    case 'not_equals':   return fv !== v;
    case 'in':           return Array.isArray(v) && v.includes(fv);
    case 'contains':     return Array.isArray(fv) && fv.includes(v);
    case 'not_contains': return !Array.isArray(fv) || !fv.includes(v);
    case 'greater_than': return Number(fv) > Number(v);
    case 'less_than':    return Number(fv) < Number(v);
    case 'between':      return Array.isArray(v) && Number(fv) >= Number(v[0]) && Number(fv) <= Number(v[1]);
    case 'before':       return fv && fv < v;
    case 'after':        return fv && fv > v;
    default:             return true;
  }
}

export const applyMemberFilters = (members, filters, legacyLogic = 'AND') => {
  if (!filters || filters.length === 0) return members;

  // If any filter after the first carries a .logic key, use per-filter evaluation
  // with standard boolean precedence: AND binds before OR.
  // Split on OR boundaries → groups; each group is ANDed; groups are ORed.
  const hasPerFilterLogic = filters.length > 1 && filters.slice(1).some(f => f.logic);

  return members.filter(member => {
    if (!hasPerFilterLogic) {
      const results = filters.map(f => matchSingleFilter(member, f));
      return legacyLogic === 'AND' ? results.every(Boolean) : results.some(Boolean);
    }
    const groups = [];
    let cur = [];
    filters.forEach((f, i) => {
      if (i > 0 && f.logic === 'OR') { groups.push(cur); cur = []; }
      cur.push(f);
    });
    groups.push(cur);
    return groups.some(g => g.every(f => matchSingleFilter(member, f)));
  });
};

// ─── Saved Filters (Members) ─────────────────────────────────────────────────

export const getSavedFilters = async (orgId) => {
  const q = query(orgCol(orgId, 'savedFilters'), orderBy('createdAt', 'desc'));
  return toArr(await getDocs(q));
};

export const saveFilter = async (orgId, filterData, adminId) => {
  const ref = await addDoc(orgCol(orgId, 'savedFilters'), {
    ...filterData,
    createdBy: adminId,
    createdAt: now(),
    updatedAt: now(),
  });
  return ref.id;
};

export const deleteFilter = async (orgId, filterId) => {
  await deleteDoc(orgDoc(orgId, 'savedFilters', filterId));
};

// ─── Saved Filters (Volunteers) ──────────────────────────────────────────────

export const getVolunteerSavedFilters = async (orgId) => {
  const q = query(orgCol(orgId, 'savedVolunteerFilters'), orderBy('createdAt', 'desc'));
  return toArr(await getDocs(q));
};

export const saveVolunteerFilter = async (orgId, filterData, adminId) => {
  const ref = await addDoc(orgCol(orgId, 'savedVolunteerFilters'), {
    ...filterData,
    createdBy: adminId,
    createdAt: now(),
    updatedAt: now(),
  });
  return ref.id;
};

export const deleteVolunteerFilter = async (orgId, filterId) => {
  await deleteDoc(orgDoc(orgId, 'savedVolunteerFilters', filterId));
};

// ─── Member CSV Export ────────────────────────────────────────────────────────

export const generateMembersCSV = (members) => {
  const headers = ['First Name', 'Last Name', 'Email', 'Phone', 'District',
                   'Status', 'Engagement Tier', 'Date Joined', 'Last Active'];
  const rows = members.map(m => [
    m.firstName || '',
    m.lastName  || '',
    m.email     || '',
    m.phone     || '',
    m.district  || '',
    m.status    || '',
    m.engagementTier || '',
    m.dateJoined     || '',
    m.lastActiveAt ? fmtDate(m.lastActiveAt) : '',
  ]);
  return [headers, ...rows]
    .map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(','))
    .join('\n');
};

// ─── Volunteer CSV Export ─────────────────────────────────────────────────────

export const generateVolunteersCSV = (volunteers) => {
  const headers = ['Name', 'Email', 'Phone', 'District', 'Status',
                   'Skills', 'Languages', 'Total Hours', 'Total Shifts', 'Date Joined'];
  const rows = volunteers.map(v => [
    v.displayName            || '',
    v.email                  || '',
    v.phone                  || '',
    v.district               || '',
    v.status                 || '',
    (v.skills     || []).join('; '),
    (v.languages  || []).join('; '),
    (v.totalHours || 0).toFixed(1),
    v.totalShiftsCompleted   || 0,
    v.createdAt ? fmtDate(v.createdAt) : '',
  ]);
  return [headers, ...rows]
    .map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(','))
    .join('\n');
};
